import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'types';
import {
  isAddon,
  isBundle,
  isSeat,
  isTicket,
  isTopup,
  SelectedAddon,
  SelectedBundle,
  SelectedSeat,
  SelectedTicket,
} from 'types/SelectedItem';
import { getTicketTypes } from 'utils/bundles';
import {
  getPriceWithoutTax,
  getTaxPrice,
  hasUnbundledAddons,
} from 'utils/tickets';
import { initialState } from '.';

const selectSlice = (state: RootState) => state.ticketSale || initialState;

export const selectTicketSalePageData = createSelector(
  [selectSlice],
  state => state,
);

export const selectTicketSaleIsLoading = createSelector(
  [selectSlice],
  state => state.loading,
);

export const makeSelectedEvent = createSelector(
  [selectSlice],
  state => state.selectedEvent,
);

export const selectSelectedItems = createSelector(
  [selectSlice],
  state => state.selectedItems,
);

export const selectSelectedTickets = createSelector([selectSlice], state =>
  state.selectedItems?.filter(
    (item): item is SelectedTicket | SelectedSeat =>
      isTicket(item) || isSeat(item),
  ),
);

export const selectSelectedBundles = createSelector([selectSlice], state =>
  state.selectedItems?.filter((item): item is SelectedBundle => isBundle(item)),
);

export const selectSelectedAddons = createSelector([selectSlice], state =>
  state.selectedItems.filter((item): item is SelectedAddon => isAddon(item)),
);

// we can have the same addon selected for multiple tickets
// so this 'select' merges all addon types in a CartItem 'structure'
// note: used only for display purposes
export const selectMergedSelectedAddons = createSelector(
  [selectSlice],
  state => {
    const mergedAddons: SelectedAddon[] = [];
    const { selectedItems } = state;

    for (let i = 0; i < selectedItems.length; i++) {
      if (isAddon(selectedItems[i])) {
        const indexOfCurrentAddonInMergedAddons = mergedAddons.findIndex(
          addon =>
            isAddon(addon) &&
            addon.addonId === (selectedItems[i] as SelectedAddon).addonId &&
            addon.ticketId === (selectedItems[i] as SelectedAddon).ticketId,
        );

        if (indexOfCurrentAddonInMergedAddons >= 0) {
          mergedAddons[indexOfCurrentAddonInMergedAddons].quantity +=
            selectedItems[i].quantity;
        } else {
          mergedAddons.push({ ...(selectedItems[i] as SelectedAddon) });
        }
      }
    }

    return mergedAddons;
  },
);

export const selectTotalPriceOfTopUps = createSelector([selectSlice], state => {
  const {
    selectedItems,
    selectedEvent: { tax_rate, topup_tax_included },
  } = state;
  let sum = 0;

  for (let i = 0; i < selectedItems.length; i++) {
    if (isTopup(selectedItems[i])) {
      sum += selectedItems[i].price;
    }
  }

  const sumTotalWithoutTax = getPriceWithoutTax(sum, tax_rate.rate);

  return {
    sum: topup_tax_included ? sumTotalWithoutTax : sum,
    tax: topup_tax_included
      ? getTaxPrice(sumTotalWithoutTax, tax_rate.rate)
      : getTaxPrice(sum, tax_rate.rate),
  };
});

export const selectWalletCredit = createSelector(
  [selectSlice],
  state => state.selectedEvent.walletcredit?.[0],
);

export const selectWhichChecksIfThereAreUnbundledAddonsOrTopUpForTheSelectedItems =
  createSelector(
    [selectSlice],
    state =>
      state.selectedEvent.walletcredit?.length ||
      state.selectedItems.some(item => {
        if (isTicket(item)) {
          const ticketType = state.selectedEvent.tickets.find(
            ({ id }) => id === item.ticketId,
          );

          // if the ticket type is not found, it means that selectedItems array contains
          // tickets from multiple events. same below for bundles
          if (!ticketType) {
            return false;
          }

          return hasUnbundledAddons(ticketType);
        } else if (isBundle(item)) {
          const bundle = state.selectedEvent.ticketTypeBundles?.find(
            ({ id }) => id === item.id,
          );

          if (!bundle) {
            return false;
          }

          const ticketTypes = getTicketTypes(
            bundle.items,
            state.selectedEvent.tickets,
          );

          for (const ticketType of ticketTypes) {
            if (hasUnbundledAddons(ticketType)) {
              return true;
            }
          }
        }
        return false;
      }),
  );

export const selectVoucherData = createSelector(
  [selectSlice],
  state => state.voucher,
);

export const selectEventCurrency = createSelector(
  [selectSlice],
  state => state.selectedEvent.currency,
);

export const selectTicketIdsFromQueryParams = createSelector(
  [selectSlice],
  state => state.ticketIdsFromQueryParams,
);
